import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { GetUsersResponseEntry } from '@/services/api/api.types'

import { useGlobalState } from './useGlobalState'

export function useAllContentCreators(): {
  allContentCreators: GetUsersResponseEntry[]
  isLoaded: boolean
} {
  const { api, authenticationStore } = useGlobalState()
  const { isSponsorAdmin } = authenticationStore

  // The /members/get endpoint requires admin permissions, so SMEs get an empty
  // array and consumers fall back to inline additionalOwners / raw owner fields.
  const smeUsersQuery = useQuery({
    queryKey: ['users', 'sme'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, role: 'SME' }),
    enabled: isSponsorAdmin,
    staleTime: 60_000,
  })
  const adminUsersQuery = useQuery({
    queryKey: ['users', 'admin'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, role: 'Admin' }),
    enabled: isSponsorAdmin,
    staleTime: 60_000,
  })
  const swiAdminUsersQuery = useQuery({
    queryKey: ['users', 'swi-admin'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, role: 'SWI Admin' }),
    enabled: isSponsorAdmin,
    staleTime: 60_000,
  })

  const allContentCreators = useMemo(() => {
    if (!isSponsorAdmin) return []

    const allUsers = [
      ...(smeUsersQuery.data?.members ?? []),
      ...(adminUsersQuery.data?.members ?? []),
      ...(swiAdminUsersQuery.data?.members ?? []),
    ]

    return allUsers.filter((user, index, array) => array.findIndex(u => u.userId === user.userId) === index)
  }, [isSponsorAdmin, smeUsersQuery.data, adminUsersQuery.data, swiAdminUsersQuery.data])

  const isLoaded = isSponsorAdmin
    ? smeUsersQuery.isSuccess && adminUsersQuery.isSuccess && swiAdminUsersQuery.isSuccess
    : true

  return { allContentCreators, isLoaded }
}
